import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BoardStageSummary,
  BoardStage,
  BoardStageRequest,
} from '@board/types/board-stage';
import { environment } from '@environment';
import { Page } from '@longnecktech/splash-commons-fe';
import { NotificationService } from '@services/notification.service';
import { GetRequestParams } from '@shared/types/request-params';
import { createParamsWithPagination } from '@shared/utils/http.util';
import { addNewItemToState, updateItemInState } from '@shared/utils/state.util';
import { BehaviorSubject, catchError, throwError, Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BoardStageService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  private _baseUrl = environment.backendUrl + '/api/admin/board/stages';
  private _summary = new BehaviorSubject<BoardStageSummary[]>([]);
  private _stages = new BehaviorSubject<BoardStage[]>([]);
  private _isLoading = new BehaviorSubject<boolean>(false);
  private _totalElements = new BehaviorSubject<number>(0);

  readonly summary$ = this._summary.asObservable();
  readonly stages$ = this._stages.asObservable();
  readonly totalElements$ = this._totalElements.asObservable();
  readonly isLoading$ = this._isLoading.asObservable();

  constructor() {}

  fetchAllSummary(): Observable<BoardStageSummary[]> {
    this._isLoading.next(true);
    return this.http.get<BoardStageSummary[]>(`${this._baseUrl}/summary`).pipe(
      tap((res) => {
        this._isLoading.next(false);
        this._summary.next(res);
      }),
      catchError((err) => {
        this._isLoading.next(false);
        return throwError(() => new Error(err.message));
      }),
    );
  }

  fetchStages(params?: GetRequestParams): Observable<Page<BoardStage>> {
    if (!params) {
      params = {
        page: 0,
        pageSize: 50,
      };
    }

    const httpParams = createParamsWithPagination(params);
    this._isLoading.next(true);

    return this.http
      .get<Page<BoardStage>>(this._baseUrl, { params: httpParams })
      .pipe(
        tap((response) => {
          this._isLoading.next(false);
          this._stages.next(response.content);
          this._totalElements.next(response.totalElements);
        }),
        catchError((err) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  fetchStageByUuid(uuid: string): Observable<BoardStage> {
    this._isLoading.next(true);
    return this.http
      .get<BoardStage>(`${this._baseUrl}/${uuid}`)
      .pipe(finalize(() => this._isLoading.next(false)));
  }

  createStage(stage: BoardStageRequest): Observable<BoardStage> {
    this._isLoading.next(true);
    return this.http.post<BoardStage>(this._baseUrl, stage).pipe(
      tap((data) => {
        this._isLoading.next(false);
        addNewItemToState(this._stages, data);
        this._totalElements.next(this._totalElements.value + 1);
        this.notificationService.showNotification('stage.createSuccess');
      }),
      catchError((error) => {
        this._isLoading.next(false);
        this.notificationService.showNotification('stage.createError');
        return throwError(() => error);
      }),
    );
  }

  updateStage(stage: BoardStageRequest, uuid: string): Observable<BoardStage> {
    this._isLoading.next(true);
    return this.http.put<BoardStage>(`${this._baseUrl}/${uuid}`, stage).pipe(
      tap((data) => {
        this._isLoading.next(false);
        updateItemInState(this._stages, data);
        this.notificationService.showNotification('stage.updateSuccess');
      }),
      catchError((error) => {
        this._isLoading.next(false);
        this.notificationService.showNotification('stage.updateError');
        return throwError(() => error);
      }),
    );
  }

  resetData(): void {
    this._summary.next([]);
  }
}
